import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, House } from 'lucide-react';
import { apiEndpoint } from '../config/api';

const PROFILE_API_URL = process.env.REACT_APP_ME_API_URL || apiEndpoint('api/me.php');
const BACKGROUND_URL = 'https://st.quantrimang.com/photos/image/2025/03/31/Anh-nen-den-13.jpg';
const LOGO_URL = 'https://static.vecteezy.com/system/resources/thumbnails/024/553/534/small/lion-head-logo-mascot-wildlife-animal-illustration-generative-ai-png.png';

const UpdateHoSoPage = () => {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    taxCode: '',
    representative: '',
    address: '',
    phone: '',
    capital: '',
    foundingDate: '',
    mainBusiness: '',
    bankName: '',
    bankAccount: ''
  });
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState('');

  useEffect(() => {
    const loadProfile = async () => {
      const token =
        sessionStorage.getItem('demo_auth_token') ||
        localStorage.getItem('demo_auth_token');

      if (!token) {
        setProfileError('Chưa đăng nhập.');
        setIsLoadingProfile(false);
        return;
      }

      try {
        const response = await fetch(PROFILE_API_URL, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          },
          cache: 'no-store'
        });

        const data = await response.json();

        if (!response.ok || !data.success || !data.user) {
          throw new Error(data.message || 'Không thể tải thông tin hồ sơ.');
        }

        const user = data.user;

        const fullAddress = [
          user.address,
          user.district,
          user.city
        ].filter(Boolean).join(', ');

        setFormData({
          businessName: user.company || '',
          taxCode: user.taxCode || '',
          representative: user.ownerName || '',
          address: fullAddress || '',
          phone: user.phone || '',
          capital:
            user.capital !== null &&
            user.capital !== undefined &&
            user.capital !== ''
              ? Number(user.capital).toLocaleString('vi-VN')
              : '',
          foundingDate: user.createdAt
            ? String(user.createdAt)
                .slice(0, 10)
                .split('-')
                .reverse()
                .join('/')
            : '',
          mainBusiness: user.mainBusiness || '',
          bankName: user.bankName || '',
          bankAccount: user.accountNumber || ''
        });

        setProfileError('');
      } catch (error) {
        console.error('Error loading profile:', error);
        setProfileError(error.message || 'Không thể tải thông tin hồ sơ.');
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadProfile();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    
    // Format as DD/MM/YYYY
    if (value.length >= 2) {
      value = value.slice(0, 2) + '/' + value.slice(2);
    }
    if (value.length >= 5) {
      value = value.slice(0, 5) + '/' + value.slice(5);
    }
    if (value.length > 10) {
      value = value.slice(0, 10);
    }
    
    setFormData(prev => ({
      ...prev,
      foundingDate: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Save form data to localStorage
    localStorage.setItem('userBusinessInfo', JSON.stringify(formData));
    console.log('Form data:', formData);
    navigate('/loading');
  };

  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: 'Arial' }}>
      {/* Header */}
      <header className="relative bg-gradient-primary">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 w-12">
            <button
              onClick={() => navigate('/home')}
              className="p-2 rounded-lg text-primary-foreground hover:bg-white/10 transition-colors"
              aria-label="Quay lại"
            >
              <ArrowLeft className="h-8 w-8" />
            </button>
          </div>
          <div className="flex flex-col items-center">
            <img
              src={require('../assets/thuedinetu-BoU6E12Y.png')}
              alt="Logo"
              className="h-20 w-auto object-contain"
            />
          </div>
          <div className="flex items-center gap-1 w-12 justify-end">
            <button
              onClick={() => setShowPhoneDialog(true)}
              className="p-2 rounded-lg text-primary-foreground hover:bg-white/10 transition-colors relative"
              aria-label="Thông báo"
            >
              <div className="relative">
                <Bell className="h-8 w-8" />
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white border-2 border-primary-foreground">
                  1
                </span>
              </div>{/* Dialog Content */}
          
            </button>
            <button
              onClick={() => navigate('/home')}
              className="p-2 rounded-lg text-primary-foreground hover:bg-white/10 transition-colors"
              aria-label="Trang chủ"
            >
              <House className="h-8 w-8" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="mobile-container relative min-h-screen bg-background pb-2">
        {/* Background Image */}
        <div
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat pointer-events-none"
          style={{
            backgroundImage: `url(${BACKGROUND_URL})`,
            opacity: 2
          }}
        />

        <div className="min-h-screen relative">
          {/* Purple Gradient Top */}
          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-primary -z-10" />

          {/* Decorative Circles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
            <div className="absolute right-0 top-1/4 w-64 h-64 opacity-10">
              <div className="w-full h-full rounded-full border-8 border-accent"></div>
            </div>
            <div className="absolute left-0 bottom-1/4 w-48 h-48 opacity-10">
              <div className="w-full h-full rounded-full border-8 border-accent"></div>
            </div>
          </div>

          {/* Form Container */}
          <div className="px-4 pt-4 pb-8">
            <div
              className="bg-card rounded-2xl p-5 shadow-lg"
              style={{
                backgroundImage: `url(${require('../assets/trong-CK13KxDf.png')})`,
                backgroundPosition: 'center center',
                backgroundSize: 'auto',
                backgroundRepeat: 'repeat'
              }}
            >
              <h2 className="text-[22px] font-bold text-foreground mb-6">
                Thông tin doanh nghiệp
              </h2>

              {isLoadingProfile && (
                <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                  Đang tải thông tin từ hệ thống...
                </div>
              )}

              {profileError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {profileError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1">
                  <label className="text-[16px] font-medium text-foreground">
                    Tên doanh nghiệp
                  </label>
                  <input
                    type="text"
                    name="businessName"
                    placeholder="Chưa cập nhật"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    className="flex w-full border px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-11 bg-background border-border rounded-lg"
                  />
                </div>

                <div className="space-y-1">
                  <label 
                    className="text-[16px] font-medium text-foreground cursor-pointer hover:opacity-70"
                    onClick={() => setShowDialog(true)}
                  >
                    Mã số thuế
                  </label>
                  <input
                    type="text"
                    name="taxCode"
                    placeholder="Chưa cập nhật"
                    value={formData.taxCode}
                    onChange={handleInputChange}
                    className="flex w-full border px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-11 bg-background border-border rounded-lg"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[16px] font-medium text-foreground">
                    Người đại diện
                  </label>
                  <input
                    type="text"
                    name="representative"
                    placeholder="Chưa cập nhật"
                    value={formData.representative}
                    onChange={handleInputChange}
                    className="flex w-full border px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-11 bg-background border-border rounded-lg"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[16px] font-medium text-foreground">
                    Địa chỉ trụ sở
                  </label>
                  <input
                    type="text"
                    name="address"
                    placeholder="Chưa cập nhật"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="flex w-full border px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-11 bg-background border-border rounded-lg"
                  />
                </div>

                <div className="space-y-1">
                  <label 
                    className="text-[16px] font-medium text-foreground cursor-pointer hover:opacity-70"
                    onClick={() => setShowPhoneDialog(true)}
                  >
                    Số điện thoại
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Chưa cập nhật"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="flex w-full border px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-11 bg-background border-border rounded-lg"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[16px] font-medium text-foreground">
                    Vốn điều lệ (VNĐ)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="capital"
                      value={formData.capital}
                      onChange={handleInputChange}
                      className="flex w-full border px-3 py-2 pr-16 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-11 bg-background border-border rounded-lg"
                    />
                    {formData.capital && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-foreground pointer-events-none">
                        VNĐ
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[16px] font-medium text-foreground">
                    Ngày thành lập
                  </label>
                  <input
                    type="text"
                    name="foundingDate"
                    placeholder="Chưa cập nhật"
                    value={formData.foundingDate}
                    onChange={handleDateChange}
                    maxLength="10"
                    className="flex w-full border px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-11 bg-background border-border rounded-lg"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[16px] font-medium text-foreground">
                    Ngành nghề chính
                  </label>
                  <input
                    type="text"
                    name="mainBusiness"
                    placeholder="Chưa cập nhật"
                    value={formData.mainBusiness}
                    onChange={handleInputChange}
                    className="flex w-full border px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-11 bg-background border-border rounded-lg"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[16px] font-medium text-foreground">
                    Tên ngân hàng
                  </label>
                  <input
                    type="text"
                    name="bankName"
                    placeholder="Chưa cập nhật"
                    value={formData.bankName}
                    onChange={handleInputChange}
                    className="flex w-full border px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-11 bg-background border-border rounded-lg"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[16px] font-medium text-foreground">
                    Số tài khoản
                  </label>
                  <input
                    type="text"
                    name="bankAccount"
                    placeholder="Chưa cập nhật"
                    value={formData.bankAccount}
                    onChange={handleInputChange}
                    className="flex w-full border px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-11 bg-background border-border rounded-lg"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoadingProfile}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-[16px] ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 px-6 py-3 w-full h-14 rounded-full bg-[#C91D39] hover:bg-[#A01730] text-white font-semibold mt-6"
                >
                  Cập nhật
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog */}
      {showDialog && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowDialog(false)}
          />
          
          {/* Dialog Content */}
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md">
            <div className="bg-white rounded-2xl p-6 shadow-xl relative">
              <button
                onClick={() => setShowDialog(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-light"
              >
                ×
              </button>
              
              <p className="text-center text-foreground text-base leading-relaxed mb-6 pr-6">
                Đây là tài khoản mới đăng ký chưa có hạn mức giao dịch tại ngân hàng. Vui lòng thực hiện giao dịch để có lịch sử giao dịch kê khai doanh nghiệp.
              </p>
              
              <button
                onClick={() => setShowDialog(false)}
                className="w-full bg-[#9A0001] hover:bg-[#800001] text-white font-semibold py-3 rounded-lg transition-colors"
              >
                Tiếp tục
              </button>
            </div>
          </div>
        </>
      )}

      {/* Phone Dialog */}
      {showPhoneDialog && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowPhoneDialog(false)}
          />
          
          {/* Dialog Content */}
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md">
            <div className="bg-white rounded-2xl p-6 shadow-xl relative">
              <button
                onClick={() => setShowPhoneDialog(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-light"
              >
                ×
              </button>
              
              <p className="text-center text-foreground text-base leading-relaxed mb-6 pr-6">
                Vui lòng kê khai tối thiểu 1 ~ 10 % so với vốn điều lệ đã đăng ký của công ty. Liên hệ cán bộ quản lý khu vực để được hướng dẫn.
              </p>
              
              <button
                onClick={() => setShowPhoneDialog(false)}
                className="w-full bg-[#9A0001] hover:bg-[#800001] text-white font-semibold py-3 rounded-lg transition-colors"
              >
                Tiếp tục
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UpdateHoSoPage;
